import type { LayoutComponentType } from '@web-studio/shared-schemas';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LabModeBar } from '@/components/LabModeBar';
import { MatrixToolbar } from '@/components/MatrixToolbar';
import { MatrixWorkbench } from '@/components/MatrixWorkbench';
import { useBlockFixtureEditor } from '@/hooks/useFixtureBlocks';
import {
  applyMatrixAxesToBlock,
  matrixStateToSearchParams,
  parseLabChromeParams,
  parseMatrixSearchParams,
} from '@/lib/matrixState';

export function BlockIsolatePage() {
  const { componentType: routeType } = useParams<{ componentType: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const state = useMemo(
    () =>
      parseMatrixSearchParams(
        searchParams,
        (routeType as LayoutComponentType) ?? 'Hero_Editorial_Split',
      ),
    [searchParams, routeType],
  );

  const chrome = useMemo(() => parseLabChromeParams(searchParams), [searchParams]);
  const { blocks, patchContent, patchMedia, reset } = useBlockFixtureEditor(state.fixtureId);

  const previewBlocks = useMemo(
    () => blocks.map((block) => applyMatrixAxesToBlock(block, state)),
    [blocks, state],
  );

  const syncUrl = useCallback(
    (patch: Partial<typeof state> & { lab?: Partial<typeof chrome> }) => {
      const next = { ...state, ...patch };
      const labPatch = patch.lab ?? {};
      const nextChrome = { ...chrome, ...labPatch };
      const qs = matrixStateToSearchParams(next);
      if (nextChrome.engineer) qs.set('mode', 'engineer');
      else qs.delete('mode');
      if (nextChrome.editTargets) qs.set('targets', '1');
      else qs.delete('targets');
      if (nextChrome.parity) qs.set('parity', '1');
      else qs.delete('parity');
      navigate(`/matrix/block/${next.componentType}?${qs.toString()}`, { replace: true });
    },
    [navigate, state, chrome],
  );

  const exportJson = useCallback(() => {
    const block = previewBlocks[0];
    if (!block) return;
    const blob = new Blob([JSON.stringify(block, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${block.componentType}-fixture.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [previewBlocks]);

  const shareParams = useMemo(() => {
    const p = matrixStateToSearchParams(state);
    if (chrome.engineer) p.set('mode', 'engineer');
    if (chrome.editTargets) p.set('targets', '1');
    if (chrome.parity) p.set('parity', '1');
    return p;
  }, [state, chrome]);

  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/matrix/block/${state.componentType}?${shareParams.toString()}`
      : '';

  return (
    <div className="lab-page lab-fit-viewport lab-block-isolate">
      <header className="lab-fit-viewport__header">
        <h1 className="lab-fit-viewport__title">Block</h1>
        <p className="lab-fit-viewport__hint">
          Design · preview · Engineer · Block / Arrangement / Look + SSR Checkup
        </p>
      </header>

      <div className="lab-block-isolate__chrome">
        <MatrixToolbar state={state} onChange={(p) => syncUrl(p)} />
        <LabModeBar
          engineerMode={chrome.engineer}
          showEditTargets={chrome.editTargets}
          showParity={chrome.parity}
          onEngineerModeChange={(engineer) =>
            syncUrl({
              lab: {
                engineer,
                parity: engineer ? chrome.parity : false,
              },
            })
          }
          onShowEditTargetsChange={(editTargets) => syncUrl({ lab: { editTargets } })}
          onShowParityChange={(parity) => syncUrl({ lab: { parity } })}
          onExportJson={exportJson}
          onResetFixture={reset}
          exportDisabled={previewBlocks.length === 0}
          resetDisabled={previewBlocks.length === 0}
        />
      </div>

      <div className="lab-block-isolate__body">
        {previewBlocks.length > 0 ? (
          <MatrixWorkbench
            blocks={previewBlocks}
            locale={state.locale}
            themeId={state.themeId}
            colorsetId={state.colorsetId}
            spacingEnum={state.spacingEnum}
            viewport={state.viewport}
            engineerMode={chrome.engineer}
            showEditTargets={chrome.editTargets}
            showParity={chrome.parity}
            fitViewport
            onContentChange={(_blockId, fieldKey, loc, value) =>
              patchContent(fieldKey, loc, value)
            }
            onMediaChange={(_blockId, fieldKey, value) => patchMedia(fieldKey, value)}
            onShowParityChange={(parity) => syncUrl({ lab: { parity } })}
          />
        ) : (
          <p className="text-sm" style={{ color: 'var(--lab-muted)' }}>
            No fixture found. Add one in <code>packages/core-fixtures</code>.
          </p>
        )}
      </div>

      {shareUrl ? (
        <p className="lab-block-isolate__share" title={shareUrl}>
          <span>Share</span> <code>{shareUrl}</code>
        </p>
      ) : null}
    </div>
  );
}
